/**
 * Contexte Institutionnel
 * Gère le contexte global de navigation scolaire (établissement, année académique)
 *
 * Ce contexte est essentiel car de nombreuses données dépendent de:
 * - L'établissement actif (pour filtrer classes, matières, etc.)
 * - L'année académique (clé fonctionnelle pour classes, class-subjects, time-slots)
 *
 * Source de vérité: URL → localStorage → défaut serveur
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAdminStore, useAdminStoreHydrated } from '@sphere/shared/data-access/store';

// ============================================
// Types
// ============================================

export interface InstitutionalContextValue {
  // Établissement actif
  activeEstablishmentId: string | null;
  setActiveEstablishmentId: (id: string | null) => void;

  // Année académique active
  activeAcademicYear: string;
  setActiveAcademicYear: (year: string) => void;

  // Helper pour générer l'année par défaut (année en cours)
  getCurrentAcademicYear: () => string;

  // Flag si l'utilisateur a un établissement unique (déduit du bootstrap)
  hasSingleEstablishment: boolean;

  /**
   * true une fois que la résolution de `activeEstablishmentId` est fiable,
   * c'est-à-dire que le scope du bootstrap (et le store admin le cas échéant)
   * ont fini de charger. Tant que c'est `false`, `activeEstablishmentId` peut
   * valoir `null` juste parce que les données n'ont pas encore été chargées —
   * ne PAS interpréter ça comme "aucun établissement" (ex: ne pas rediriger).
   */
  isInitialized: boolean;
}

interface InstitutionalProviderProps {
  children: ReactNode;
  /** Établissements accessibles (du bootstrap.scope.establishmentIds). null = admin (tous). */
  userEstablishmentIds?: string[] | null;
  /** true une fois le bootstrap chargé — évite d'utiliser localStorage avant le scope réel */
  scopeReady?: boolean;
}

function isEstablishmentInScope(
  establishmentId: string,
  userEstablishmentIds: string[] | null | undefined,
): boolean {
  if (userEstablishmentIds === null || userEstablishmentIds === undefined) {
    return true;
  }
  return userEstablishmentIds.includes(establishmentId);
}

function resolveEstablishmentId(
  userEstablishmentIds: string[] | null | undefined,
): string | null {
  const urlEstId = new URLSearchParams(window.location.search).get(
    'establishmentId',
  );
  if (urlEstId && isEstablishmentInScope(urlEstId, userEstablishmentIds)) {
    return urlEstId;
  }

  const storedEstId = localStorage.getItem(STORAGE_KEYS.ESTABLISHMENT);
  if (
    storedEstId &&
    isEstablishmentInScope(storedEstId, userEstablishmentIds)
  ) {
    return storedEstId;
  }

  if (userEstablishmentIds === null) {
    try {
      const adminStoreData = localStorage.getItem('admin-context-storage');
      if (adminStoreData) {
        const parsed = JSON.parse(adminStoreData);
        if (parsed?.state?.currentEstablishment?.id) {
          return parsed.state.currentEstablishment.id;
        }
      }
    } catch {
      // Ignore
    }
  }

  if (userEstablishmentIds?.length === 1) {
    return userEstablishmentIds[0] ?? null;
  }

  return null;
}

// ============================================
// Context
// ============================================

const InstitutionalContext = createContext<
  InstitutionalContextValue | undefined
>(undefined);

// ============================================
// Storage keys
// ============================================

const STORAGE_KEYS = {
  ESTABLISHMENT: 'sphere:activeEstablishmentId',
  ACADEMIC_YEAR: 'sphere:activeAcademicYear',
} as const;

// ============================================
// Provider
// ============================================

// NOTE: We intentionally do NOT sync URL changes after initial load.
// The initial state is read from URL/localStorage.
// Navigation changes should NOT affect establishment/academic year context.
// If deep linking is needed, it's handled by reading URL on page load only.

export function InstitutionalProvider({
  children,
  userEstablishmentIds = null,
  scopeReady = true,
}: InstitutionalProviderProps) {
  // NOTE: On NE doit PAS utiliser useSearchParams ici, sinon tout l'arbre
  // re-render à chaque navigation. C'est délégué à InstitutionalUrlSync.

  // Helper pour lire l'URL sans s'abonner (pour l'initialisation uniquement)
  const getInitialUrlParam = (key: string): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  };

  // ============================================
  // Année académique
  // ============================================

  /**
   * Génère l'année académique par défaut
   * Format: "2024-2025"
   * Logique: si on est avant septembre, année précédente-actuelle
   * sinon année actuelle-suivante
   */
  const getCurrentAcademicYear = useCallback((): string => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11

    // Septembre = mois 8 (index 0)
    const startYear = currentMonth < 8 ? currentYear - 1 : currentYear;
    const endYear = startYear + 1;

    return `${startYear}-${endYear}`;
  }, []);

  /**
   * État de l'année académique
   * Ordre de priorité: URL > localStorage > année courante
   */
  const [activeAcademicYear, setActiveAcademicYearState] = useState<string>(
    () => {
      // 1. URL (lecture one-shot)
      const urlYear = getInitialUrlParam('year');
      if (urlYear) return urlYear;

      // 2. localStorage
      const storedYear = localStorage.getItem(STORAGE_KEYS.ACADEMIC_YEAR);
      if (storedYear) return storedYear;

      // 3. Année courante par défaut
      return getCurrentAcademicYear();
    }
  );

  /**
   * Setter qui persiste dans URL + localStorage
   */

  const setActiveAcademicYear = useCallback((year: string) => {
    setActiveAcademicYearState(year);
    localStorage.setItem(STORAGE_KEYS.ACADEMIC_YEAR, year);
    // URL update will be handled by InstitutionalUrlSync reacting to state change
  }, []);

  // ============================================
  // Établissement actif
  // ============================================

  /**
   * Détection si l'utilisateur a un seul établissement
   */
  const hasSingleEstablishment = useMemo(() => {
    if (!userEstablishmentIds) return false; // admin = tous
    return userEstablishmentIds.length === 1;
  }, [userEstablishmentIds]);

  /**
   * État de l'établissement
   */
  const [activeEstablishmentId, setActiveEstablishmentIdState] = useState<
    string | null
  >(() => (scopeReady ? resolveEstablishmentId(userEstablishmentIds) : null));

  const setActiveEstablishmentId = useCallback((id: string | null) => {
    setActiveEstablishmentIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEYS.ESTABLISHMENT, id);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ESTABLISHMENT);
    }
    // URL update delegate to InstitutionalUrlSync
  }, []);

  // ============================================
  // Sync avec le store admin (pour les admins)
  // ============================================

  const adminStoreHydrated = useAdminStoreHydrated();
  const adminEstablishment = useAdminStore(
    (state) => state.currentEstablishment
  );

  useEffect(() => {
    if (userEstablishmentIds !== null) return;
    if (!adminStoreHydrated) return;

    if (
      adminEstablishment?.id &&
      adminEstablishment.id !== activeEstablishmentId
    ) {
      setActiveEstablishmentIdState(adminEstablishment.id);
      localStorage.setItem(STORAGE_KEYS.ESTABLISHMENT, adminEstablishment.id);
    }
  }, [
    userEstablishmentIds,
    adminStoreHydrated,
    adminEstablishment?.id,
    activeEstablishmentId,
  ]);

  // Resync when bootstrap scope arrives or when localStorage/URL holds a stale ID
  useEffect(() => {
    if (!scopeReady) return;

    const resolved = resolveEstablishmentId(userEstablishmentIds);

    if (
      activeEstablishmentId &&
      userEstablishmentIds !== null &&
      userEstablishmentIds !== undefined &&
      !userEstablishmentIds.includes(activeEstablishmentId)
    ) {
      setActiveEstablishmentId(resolved);
      return;
    }

    if (!activeEstablishmentId && resolved) {
      setActiveEstablishmentId(resolved);
    }
  }, [
    scopeReady,
    userEstablishmentIds,
    activeEstablishmentId,
    setActiveEstablishmentId,
  ]);

  // ============================================
  // Initialization
  // ============================================

  // Tant que le scope du bootstrap (et le store admin pour les admins) n'a
  // pas fini de charger, on ne peut pas savoir avec certitude si
  // `activeEstablishmentId` vaut `null` parce que l'utilisateur n'a vraiment
  // aucun établissement, ou juste parce que les données ne sont pas encore là.
  const isInitialized = useMemo(() => {
    if (!scopeReady) return false;
    if (userEstablishmentIds === null) return adminStoreHydrated;
    return true;
  }, [scopeReady, userEstablishmentIds, adminStoreHydrated]);

  // ============================================
  // Context value
  // ============================================

  const value = useMemo<InstitutionalContextValue>(
    () => ({
      activeEstablishmentId,
      setActiveEstablishmentId,
      activeAcademicYear,
      setActiveAcademicYear,
      getCurrentAcademicYear,
      hasSingleEstablishment,
      isInitialized,
    }),
    [
      activeEstablishmentId,
      setActiveEstablishmentId,
      activeAcademicYear,
      setActiveAcademicYear,
      getCurrentAcademicYear,
      hasSingleEstablishment,
      isInitialized,
    ]
  );

  return (
    <InstitutionalContext.Provider value={value}>
      {children}
    </InstitutionalContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook pour accéder au contexte institutionnel
 *
 * @example
 * ```tsx
 * const { activeEstablishmentId, activeAcademicYear } = useInstitutional();
 *
 * // Utiliser dans une query
 * const { data: classes } = useClasses({
 *   establishmentId: activeEstablishmentId,
 *   academicYear: activeAcademicYear,
 * });
 * ```
 */
export function useInstitutional(): InstitutionalContextValue {
  const context = useContext(InstitutionalContext);

  if (!context) {
    throw new Error(
      'useInstitutional must be used within InstitutionalProvider'
    );
  }

  return context;
}

/**
 * Hook spécialisé pour obtenir l'année académique active
 */
export function useActiveAcademicYear(): string {
  const { activeAcademicYear } = useInstitutional();
  return activeAcademicYear;
}

/**
 * Hook spécialisé pour obtenir l'établissement actif
 */
export function useActiveEstablishment(): string | null {
  const { activeEstablishmentId } = useInstitutional();
  return activeEstablishmentId;
}
